import React, { useState, useMemo, useEffect } from 'react';
import { Button, Tooltip, OverlayTrigger } from 'react-bootstrap';
import QRCode from './QRCode';
import FabMenu from './components/FabMenu';
import LoginModal from './components/LoginModal';
import { useAuth } from './contexts/AuthContext';
import { auth } from './firebase';
import { trackInteraction } from './api/interactions';
import { saveCard } from './api/card';
import './Card.css';

const IconLink = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" fill="currentColor" viewBox="0 0 24 24">
    <path d="M13.06 8.94a5.5 5.5 0 0 0-7.78 0L2.1 12.12a5.5 5.5 0 0 0 7.78 7.78l1.83-1.83a.75.75 0 1 0-1.06-1.06l-1.83 1.83a4 4 0 0 1-5.66-5.66l3.18-3.18a4 4 0 0 1 5.66 0 .75.75 0 1 0 1.06-1.06Z"/>
    <path d="M21.9 11.88a5.5 5.5 0 0 0-7.78-7.78L12.29 5.93a.75.75 0 1 0 1.06 1.06l1.83-1.83a4 4 0 0 1 5.66 5.66l-3.18 3.18a4 4 0 0 1-5.66 0 .75.75 0 1 0-1.06 1.06 5.5 5.5 0 0 0 7.78 0l3.18-3.18Z"/>
  </svg>
);

const IconLinkBreak = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" fill="currentColor" viewBox="0 0 24 24">
    <path d="M8.94 13.06a.75.75 0 0 0 1.06 1.06l1-1a.75.75 0 0 0-1.06-1.06l-1 1ZM12 4.75a.75.75 0 0 0-1.5 0v1.5a.75.75 0 0 0 1.5 0v-1.5ZM4.75 12a.75.75 0 0 0 0 1.5h1.5a.75.75 0 0 0 0-1.5h-1.5ZM12 18.75a.75.75 0 0 0 1.5 0v-1.5a.75.75 0 0 0-1.5 0v1.5ZM18.75 11a.75.75 0 0 0 0 1.5h1.5a.75.75 0 0 0 0-1.5h-1.5Z"/>
    <path d="M13.06 8.94a5.5 5.5 0 0 0-7.78 0L2.1 12.12a5.5 5.5 0 0 0 7.78 7.78l1.2-1.2a.75.75 0 1 0-1.06-1.06l-1.2 1.2a4 4 0 0 1-5.66-5.66l3.18-3.18a4 4 0 0 1 5.66 0 .75.75 0 1 0 1.06-1.06Z"/>
    <path d="M21.9 11.88a5.5 5.5 0 0 0-7.78-7.78l-1.2 1.2a.75.75 0 1 0 1.06 1.06l1.2-1.2a4 4 0 0 1 5.66 5.66l-3.18 3.18a4 4 0 0 1-5.66 0 .75.75 0 1 0-1.06 1.06 5.5 5.5 0 0 0 7.78 0l3.18-3.18Z"/>
  </svg>
);

const Icon = ({ name }) => {
  const icons = {
    back: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/></svg>,
    print: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M2.5 8a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/><path d="M5 1a2 2 0 0 0-2 2v2H2a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1v1a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-1h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1V3a2 2 0 0 0-2-2H5zM4 3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2H4V3zm1 5a2 2 0 0 0-2 2v1H2a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v-1a2 2 0 0 0-2-2H5zm7 2v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1z"/></svg>,
  };
  return icons[name] || null;
};

function Step3_FinalCard({ cardData, layout, orientation, font, backgroundColor, emphasis, verticalAlign, qrOptions, onPrev, isComplete, showLogo, shortId, onShortIdChange, isOwner, publicSessionActive }) {
  const { details, displayOptions } = cardData;
  const { user, refreshCardStatus } = useAuth();
  const [isFlipped, setIsFlipped] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [saveState, setSaveState] = useState('idle'); // 'idle'|'saving'|'saved'|'error'
  const [showQrPanel, setShowQrPanel] = useState(false);
  const [pendingShare, setPendingShare] = useState(false);

  // 영상 뒷면 자동 플립: 카드 첫 방문 시 뒷면 먼저 → 2초 후 앞면
  useEffect(() => {
    const hasVideo = !!(details.videoUrl);
    if (hasVideo && isComplete) {
      setIsFlipped(true);
      const timer = setTimeout(() => setIsFlipped(false), 2200);
      return () => clearTimeout(timer);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 명함 열람 트래킹 (마운트 시 1회)
  useEffect(() => {
    const getToken = async () => user ? await user.getIdToken() : null;
    getToken().then(token => trackInteraction(shortId, 'view', token));
  }, [shortId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 비로그인 시 연락처 마스킹
  const canViewContact = !!(user || publicSessionActive);
  const maskedPhone = canViewContact ? details.phone : details.phone ? '로그인 후 확인 가능' : '';
  const maskedEmail = canViewContact ? details.email : details.email ? '로그인 후 확인 가능' : '';

  const isCompanyEmphasis = emphasis === 'company';
  const headerStyle = (orientation === 'horizontal' && verticalAlign === 'top') ? { alignSelf: 'flex-start' } : { alignSelf: 'center' };
  const contentStyle = (orientation === 'horizontal' && verticalAlign === 'top') ? { alignSelf: 'flex-start' } : { alignSelf: 'center' };
  const showHr = (displayOptions.phone || displayOptions.email || displayOptions.website || displayOptions.sns || displayOptions.address);

  const shareUrl = useMemo(() => {
    if (shortId) {
      return `${window.location.origin}/card/${shortId}`;
    }
    const displayParams = {};
    for(const key in displayOptions) {
      displayParams[`show_${key}`] = displayOptions[key];
    }
    const params = new URLSearchParams({
      step: 2,
      layout, font, emphasis, verticalAlign,
      bgColor: backgroundColor,
      complete: 'true',
      qrDotsType: qrOptions.dotsOptions.type,
      qrDotsColor: qrOptions.dotsOptions.color,
      qrBgColor: qrOptions.backgroundOptions.color,
      showLogo,
      ...details,
      ...displayParams
    });
    return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
  }, [shortId, cardData, layout, font, emphasis, verticalAlign, backgroundColor, qrOptions, showLogo]);

  // 로그인 완료되면 대기 중인 공유 액션 자동 실행
  useEffect(() => {
    if (user && pendingShare) {
      setPendingShare(false);
      handleShare();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShare = async () => {
    if (!user) {
      setPendingShare(true);
      setShowLoginModal(true);
      return;
    }

    // 저장된 shortId가 있으면 바로 /card/:shortId 링크 복사
    // 없으면 먼저 저장 후 복사
    let targetShortId = shortId;
    if (!targetShortId) {
      try {
        const idToken = await user.getIdToken();
        const safeDetails = { name: details.name, title: details.title, company: details.company, email: details.email };
        const result = await saveCard(window.location.search, '', idToken, safeDetails);
        targetShortId = result.shortId;
        localStorage.setItem('myCardShortId', targetShortId);
        onShortIdChange(targetShortId);
        // URL을 /card/:shortId 로 업데이트
        window.history.replaceState({}, '', `/card/${targetShortId}`);
      } catch {
        // 저장 실패 시 기존 URL 복사로 폴백
        navigator.clipboard.writeText(shareUrl).then(() => {
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 2000);
        });
        return;
      }
    }

    const cleanUrl = `${window.location.origin}/card/${targetShortId}`;
    navigator.clipboard.writeText(cleanUrl).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
    const token = await user.getIdToken();
    trackInteraction(targetShortId, 'share_copy', token);
  };

  const handleSaveCard = async () => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    setSaveState('saving');
    try {
      const idToken = await user.getIdToken();
      const urlParams = window.location.search;
      const safeDetails = { name: details.name, title: details.title, company: details.company, email: details.email };
      const result = await saveCard(urlParams, shortId || '', idToken, safeDetails);
      localStorage.setItem('myCardShortId', result.shortId);
      onShortIdChange(result.shortId);
      setSaveState('saved');
      refreshCardStatus(user);
      setTimeout(() => setSaveState('idle'), 2500);
    } catch {
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 2500);
    }
  };


  const CardFront = (
    <div className={`business-card ${layout} ${orientation}`} data-font={font} data-emphasis={emphasis} data-v-align={verticalAlign} style={{ backgroundColor }}>
      <div className="card-header-section" style={headerStyle}>
        {showLogo && (details.logo 
          ? <img src={details.logo} alt="logo" className="card-logo" />
          : <div className="card-logo-placeholder"><svg width="40" height="40" viewBox="0 0 100 100"><rect width="100" height="100" rx="15" fill="#E0E0E0" /></svg></div>
        )}
        <h3 className="card-name">{isCompanyEmphasis ? details.company : details.name}</h3>
        {!isCompanyEmphasis && displayOptions.company && <p className="card-company">{details.company}</p>}
      </div>
      <div className="card-content" style={contentStyle}>
        {isCompanyEmphasis 
          ? <p className="card-title">{details.name}{displayOptions.title && <span className="card-title-subtle">, {details.title}</span>}</p>
          : (displayOptions.title && <p className="card-title">{details.title}</p>)
        }
        {showHr && <hr />}
        {displayOptions.phone && (
          canViewContact
            ? <p className="card-contact"><a href={`tel:${details.phone}`} onClick={async () => { if (user) trackInteraction(shortId, 'contact_phone', await user.getIdToken()); }}>{details.phone}</a></p>
            : <p className="card-contact" style={{ color: '#aaa', cursor: 'pointer', fontSize: '0.85em' }} onClick={() => setShowLoginModal(true)}>🔒 {maskedPhone}</p>
        )}
        {displayOptions.email && (
          canViewContact
            ? <p className="card-contact"><a href={`mailto:${details.email}`} onClick={async () => { if (user) trackInteraction(shortId, 'contact_email', await user.getIdToken()); }}>{details.email}</a></p>
            : <p className="card-contact" style={{ color: '#aaa', cursor: 'pointer', fontSize: '0.85em' }} onClick={() => setShowLoginModal(true)}>🔒 {maskedEmail}</p>
        )}
        {displayOptions.website && <p className="card-contact"><a href={`https://${details.website}`} target="_blank" rel="noopener noreferrer">{details.website}</a></p>}
        {displayOptions.sns && <p className="card-contact"><a href={`https://${details.sns}`} target="_blank" rel="noopener noreferrer">{details.sns}</a></p>}
        {displayOptions.address && <p className="card-contact"><a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(details.address)}`} target="_blank" rel="noopener noreferrer">{details.address}</a></p>}
      </div>
    </div>
  );

  const resolvedVideoUrl = (() => {
    const url = details.videoUrl;
    if (!url) return null;
    if (url.includes('cloudinary.com') && url.includes('/embed/')) {
      try {
        const u = new URL(url);
        const cn = u.searchParams.get('cloud_name');
        const pid = u.searchParams.get('public_id');
        if (cn && pid) return `https://res.cloudinary.com/${cn}/video/upload/${pid}.mp4`;
      } catch {}
    }
    return url;
  })();
  const isVideoBack = !!(resolvedVideoUrl);
  const isVideoFile = resolvedVideoUrl && /\.(mp4|webm|ogg)$/i.test(resolvedVideoUrl);

  const CardBack = (
    <div
      className={`business-card back ${orientation}`}
      data-font={font}
      style={{ backgroundColor: isVideoBack ? '#111' : backgroundColor, position: 'relative', overflow: 'hidden' }}
    >
      {isVideoBack && (
        <>
          {/* 배경 미디어 */}
          {isVideoFile ? (
            <video autoPlay muted loop playsInline style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}>
              <source src={resolvedVideoUrl} type="video/mp4" />
            </video>
          ) : (
            <img src={resolvedVideoUrl} alt="background" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }} />
          )}
          {/* 시네마틱 오버레이 */}
          <div style={{ position: 'absolute', inset: 0, background: `rgba(0,0,0,${details.videoOverlay !== '' && details.videoOverlay !== undefined ? parseFloat(details.videoOverlay) : 0.38})`, zIndex: 1 }} />
          {/* 필름 그레인 */}
          <div style={{ position: 'absolute', inset: 0, opacity: 0.12, backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`, zIndex: 2, pointerEvents: 'none' }} />
          {/* 이너 프레임 */}
          <div style={{ position: 'absolute', top: 12, left: 12, right: 12, bottom: 12, border: '1px solid rgba(255,255,255,0.22)', borderRadius: 8, zIndex: 3, pointerEvents: 'none' }} />
          {/* 컨텐츠 — QR 없이 텍스트만 */}
          <div style={{ position: 'relative', zIndex: 10, display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', padding: '20px', textAlign: 'center', flexDirection: 'column' }}>
            {details.engName && (
              <h3 style={{ fontFamily: "'Pinyon Script', cursive", fontSize: '2.6rem', margin: details.tagline ? '0 0 12px 0' : '0', color: '#fff', textShadow: '0 0 12px rgba(255,255,255,0.25)', fontWeight: 'normal', lineHeight: 1.2, letterSpacing: '1px' }}>
                {details.engName}
              </h3>
            )}
            {details.tagline && (
              <>
                {details.engName && <div style={{ width: 28, height: 1, background: 'rgba(255,255,255,0.5)', margin: '0 auto 12px' }} />}
                <p style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '0.72rem', margin: 0, color: 'rgba(255,255,255,0.88)', fontWeight: 400, letterSpacing: '3px', textTransform: 'uppercase' }}>
                  {details.tagline}
                </p>
              </>
            )}
          </div>
        </>
      )}

      {!isVideoBack && (
        <>
          <div className="back-left-panel">
            {showLogo && (
              <div className="card-logo-placeholder-back">
                {details.logo
                  ? <img src={details.logo} alt="logo" className="card-logo-back" />
                  : <svg width="60" height="60" viewBox="0 0 100 100"><rect width="100" height="100" rx="15" fill="#000000" fillOpacity="0.05" /></svg>
                }
              </div>
            )}
            <p>{details.company || '회사명'}</p>
          </div>
          <div className="back-right-panel">
            <QRCode key={shareUrl} url={shareUrl} options={qrOptions} />
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      <LoginModal show={showLoginModal} onHide={() => setShowLoginModal(false)} reason={pendingShare ? 'share' : undefined} />
      <div className="step3-container">
        <div className={`flip-card ${orientation}`} onClick={() => setIsFlipped(!isFlipped)}>
          <div className={`flip-card-inner ${isFlipped ? 'flipped' : ''}`}>
            <div className="flip-card-front">{CardFront}</div>
            <div className="flip-card-back">{CardBack}</div>
          </div>
        </div>
        
        {!isComplete && <p className="text-center text-muted small mt-3">카드를 클릭하여 뒷면을 확인하세요.</p>}

        <div className="side-nav">
          {!isComplete && (
            <OverlayTrigger placement="left" overlay={<Tooltip>이전 단계</Tooltip>}>
              <Button variant="light" className="icon-button" onClick={onPrev}><Icon name="back" /></Button>
            </OverlayTrigger>
          )}
          {isVideoBack && (
            <>
              <OverlayTrigger placement="left" overlay={<Tooltip>QR 코드 보기</Tooltip>}>
                <Button variant="light" className="icon-button" onClick={() => setShowQrPanel(v => !v)}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M0 .5A.5.5 0 0 1 .5 0h3a.5.5 0 0 1 0 1H1v2.5a.5.5 0 0 1-1 0v-3zm12 0a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5v3a.5.5 0 0 1-1 0V1h-2.5a.5.5 0 0 1-.5-.5zM.5 12a.5.5 0 0 1 .5.5V15h2.5a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5v-3a.5.5 0 0 1 .5-.5zm15 0a.5.5 0 0 1 .5.5v3a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1 0-1H15v-2.5a.5.5 0 0 1 .5-.5zM4 4h1v1H4V4zm0 3h1v1H4V7zm3-3h1v1H7V4zm0 3h1v1H7V7zm3 0h1v1h-1V7zm0 3h1v1h-1v-1zm-3 0h1v1H7v-1zm0 3h1v1H7v-1zm3-6h1v1h-1V4zm3 0h1v1h-1V4zM4 10h1v1H4v-1zm0 3h1v1H4v-1z"/>
                  </svg>
                </Button>
              </OverlayTrigger>
              {showQrPanel && (
                <div style={{
                  position: 'fixed', right: 86, top: '50%', transform: 'translateY(-50%)',
                  background: '#fff', borderRadius: 12, padding: 16,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  zIndex: 100,
                }}>
                  <QRCode key={shareUrl} url={shareUrl} options={qrOptions} />
                  <span style={{ fontSize: '0.7em', color: '#aaa', letterSpacing: '1px' }}>SCAN TO CONNECT</span>
                </div>
              )}
            </>
          )}
          <OverlayTrigger placement="left" overlay={<Tooltip>{isCopied ? '복사 완료!' : '링크 복사'}</Tooltip>}>
            <Button variant="light" className="icon-button" onClick={handleShare} style={{ color: isCopied ? '#22c55e' : undefined, transition: 'color 0.2s' }}>
              {isCopied ? <IconLinkBreak /> : <IconLink />}
            </Button>
          </OverlayTrigger>
          <OverlayTrigger placement="left" overlay={<Tooltip>인쇄 / PDF로 저장</Tooltip>}><Button variant="light" className="icon-button" onClick={() => window.print()}><Icon name="print" /></Button></OverlayTrigger>
          <FabMenu cardData={cardData} shortId={shortId} idToken={user ? () => user.getIdToken() : null} />
          {/* 내 카드 저장 */}
          <OverlayTrigger placement="left" overlay={<Tooltip>
            {saveState === 'saving' ? '저장 중...' : saveState === 'saved' ? '저장 완료!' : saveState === 'error' ? '저장 실패' : '내 카드 저장'}
          </Tooltip>}>
            <Button variant="light" className="icon-button" onClick={handleSaveCard} disabled={saveState === 'saving'}>
              {saveState === 'saved'
                ? <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#22c55e" viewBox="0 0 16 16"><path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/></svg>
                : saveState === 'error'
                ? <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#ef4444" viewBox="0 0 16 16"><path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/><path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/></svg>
                : <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M2 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H9.5a1 1 0 0 0-1 1v7.293l2.646-2.647a.5.5 0 0 1 .708.708l-3.5 3.5a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L7.5 9.293V2a2 2 0 0 1 2-2H14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h2.5a.5.5 0 0 1 0 1H2z"/></svg>
              }
            </Button>
          </OverlayTrigger>
          {/* 로그인/로그아웃 버튼 */}
          <OverlayTrigger placement="left" overlay={<Tooltip>{user ? `${user.displayName || user.email} (로그아웃)` : '로그인'}</Tooltip>}>
            <Button variant="light" className="icon-button" onClick={() => user ? auth.signOut() : setShowLoginModal(true)}>
              {user
                ? <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4z"/></svg>
                : <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M6 3.5a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 .5.5v9a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5v-2a.5.5 0 0 0-1 0v2A1.5 1.5 0 0 0 6.5 14h8a1.5 1.5 0 0 0 1.5-1.5v-9A1.5 1.5 0 0 0 14.5 2h-8A1.5 1.5 0 0 0 5 3.5v2a.5.5 0 0 0 1 0v-2z"/><path fillRule="evenodd" d="M11.854 8.354a.5.5 0 0 0 0-.708l-3-3a.5.5 0 1 0-.708.708L10.293 7.5H1.5a.5.5 0 0 0 0 1h8.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3z"/></svg>
              }
            </Button>
          </OverlayTrigger>
        </div>
      </div>

      {/* Hidden area for printing */}
      <div className="print-only">
        {CardFront}
        {CardBack}
      </div>
    </>
  );
}

export default Step3_FinalCard;

